import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { LocalizationService, downloadBlob } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { UserService } from 'src/core/services/administration/user.service';
import { take } from 'rxjs';
import { ClientExcelService, ExcelRowModel } from 'src/ca-shared/client-excel/client-excel.module';
import { ImportUserInputDto } from 'src/core/models/user/import-user-input.dto';

@Component({
  selector: 'ca-user-list-import-from-file-form',
  templateUrl: './user-list-import-from-file-form.component.html',
  styleUrl: './user-list-import-from-file-form.component.scss',
  standalone: false,
})
export class UserListImportFromFileFormComponent implements OnInit {
  @Output()
  imported = new EventEmitter();

  @Output()
  closed = new EventEmitter();

  constructor(
    private userService: UserService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private confirmationService: ConfirmationService,
    private excelService: ClientExcelService
  ) {}

  public selectedCsvFile: File = null;

  acceptFileExtensions: string[] = ['.csv'];
  maxFileSize: number = 10 * 1024 * 1024;
  formData = new FormData();
  isLoading: boolean = false;

  ngOnInit(): void {}

  downloadExampleFile() {
    const fileName = this.localizationService.instant('User::ImportUsersSampleFileName');
    this.userService.downloadExampleFile().subscribe(result => {
      if (result.size > 0) {
        downloadBlob(result, fileName + '.csv');
      }
    });
  }

  onFileChange(files) {
    if (files.length > 0) {
      const splitName = files.item(0).name.split('.');
      if (files.item(0).size > this.maxFileSize) {
        this.selectedCsvFile = null;
        this.confirmationService
          .warn('User::FileSizeTooLarge', '::Warning', {
            yesText: '::Ok',
            hideCancelBtn: true,
          })
          .pipe(take(1))
          .subscribe();
        return;
      }
      if (this.acceptFileExtensions.indexOf('.' + splitName[splitName.length - 1]) <= -1) {
        this.selectedCsvFile = null;
        this.toastr.error(
          this.localizationService.instant(
            '::FileExtensionError',
            this.acceptFileExtensions.join(',')
          )
        );
        return;
      }
      this.selectedCsvFile = files.item(0);
      return;
    }
    this.selectedCsvFile = null;
  }

  async onImportFromFile() {
    this.formData = new FormData();
    if (this.selectedCsvFile && !this.isLoading) {
      this.isLoading = true;
      this.formData.append('csvFile', this.selectedCsvFile, this.selectedCsvFile.name);
      this.userService.importFromFile(this.formData).subscribe({
        next: result => {
          const successCount = result?.successCount ?? 0;
          const showSuccessMessage = () => {
            const successTemplate = this.localizationService.instant(
              'User::SuccessfullyImportMessage'
            );
            const message = successTemplate.replace('{0}', successCount.toString());
            this.toastr.success(message);
            this.isLoading = false;
            this.imported.emit();
          };

          if (result?.failedCount > 0) {
            this.confirmationService
              .warn('User::InvalidUsersMessage', '::DefaultConfirmationMessage', {
                messageLocalizationParams: [
                  result?.successCount.toString(),
                  result?.failedCount.toString(),
                ],
                yesText: '::Yes',
              })
              .pipe(take(1))
              .subscribe((status: Confirmation.Status) => {
                if (status === Confirmation.Status.confirm) {
                  this.downloadCsv(result.failedUsers);
                }
                showSuccessMessage();
              });
          } else {
            showSuccessMessage();
          }
        },
        error: error => {
          this.isLoading = false;
          this.toastr.error(error);
        },
        complete: () => {
          this.formData.delete('csvFile');
        },
      });
      this.formData.delete('csvFile');
    } else {
      this.isLoading = true;
      this.confirmationService
        .warn('User::UploadFileMessage', '::Warning', {
          yesText: '::Ok',
          hideCancelBtn: true,
        })
        .pipe(take(1))
        .subscribe((status: Confirmation.Status) => {
          if (status === Confirmation.Status.confirm) {
            this.isLoading = false;
          }
        });
    }
  }

  downloadCsv(data: ImportUserInputDto[]) {
    if (!data || !data.length) return;

    const headerMap: Record<string, string> = {
      errorReason: this.localizationService.instant('User::ImportExportField:ErrorReason'),
      userName: this.localizationService.instant('User::ImportExportField:UserName'),
      name: this.localizationService.instant('User::ImportExportField:Name'),
      middleName: this.localizationService.instant('User::ImportExportField:MiddleName'),
      surname: this.localizationService.instant('User::ImportExportField:Surname'),
      emailAddress: this.localizationService.instant('User::ImportExportField:EmailAddress'),
      password: this.localizationService.instant('User::ImportExportField:Password'),
      roles: this.localizationService.instant('User::ImportExportField:Roles'),
      departmentId: this.localizationService.instant('User::ImportExportField:DepartmentId'),
      phoneNumber1: this.localizationService.instant('User::ImportExportField:PhoneNumber1'),
      phoneNumber2: this.localizationService.instant('User::ImportExportField:PhoneNumber2'),
      pbxAgentId: this.localizationService.instant('User::ImportExportField:PbxAgentId'),
      externalId: this.localizationService.instant('User::ImportExportField:ExternalId'),
      isActive: this.localizationService.instant('User::ImportExportField:IsActive'),
      lockAfterFailedAttempts: this.localizationService.instant(
        'User::ImportExportField:LockAfterFailedAttempts'
      ),
    };

    const headerRow = Object.keys(headerMap).map(key => ({
      value: headerMap[key],
    }));

    const rows: ExcelRowModel[] = [];
    rows.push({ cells: headerRow });

    data.forEach(row => {
      rows.push({
        cells: Object.keys(headerMap).map(key => {
          let value = row[key] ?? '';

          if (Array.isArray(value)) {
            value = value.join(',');
          }

          return { value };
        }),
      });
    });
    const fileName = this.localizationService.instant('User::InvalidUsersFileName') + '.csv';
    this.excelService.generateAndDownload(
      {
        fileName,
        worksheets: [
          {
            title: fileName,
            properties: { defaultColWidth: 25 },
            rows,
          },
        ],
      },
      true
    );
  }

  closePanel() {
    this.closed.emit();
  }
}
